import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Input, Table } from "reactstrap";
import { toast } from "react-toastify";
import { FaDownload, FaEnvelope, FaTrash, FaUserSlash } from "react-icons/fa";
import Helmet from "../../components/Helmet/Helmet";
import AdminPageShell, { AdminSurface } from "../../components/Admin/AdminPageShell";
import { getApiUrl } from "../../utils/site";

const SubscribeManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/email/subscribers"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscribers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Failed to fetch subscribers.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Are you sure you want to unsubscribe ${email}?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        getApiUrl("/api/email/unsubscribe"),
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User unsubscribed successfully.");
      fetchSubscribers();
    } catch (error) {
      toast.error("Failed to unsubscribe user.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscriber?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(getApiUrl(`/api/email/subscribers/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Subscriber deleted successfully.");
      fetchSubscribers();
    } catch (error) {
      toast.error("Failed to delete subscriber.");
    }
  };

  const filteredSubscribers = useMemo(() => {
    return subscribers.filter((subscriber) => {
      const email = String(subscriber.email || "").toLowerCase();
      const name = String(subscriber.name || "").toLowerCase();
      const status = String(subscriber.status || "").toLowerCase();
      const query = searchTerm.toLowerCase();
      const matchesSearch = email.includes(query) || name.includes(query);
      const matchesStatus = filterStatus === "all" || status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [filterStatus, searchTerm, subscribers]);

  const activeCount = subscribers.filter((subscriber) => subscriber.status === "active").length;
  const inactiveCount = subscribers.filter((subscriber) => subscriber.status === "unsubscribed").length;
  const engagementRate = subscribers.length ? Math.round((activeCount / subscribers.length) * 100) : 0;

  const exportSubscribers = () => {
    const csvContent = [
      ["Email", "Name", "Status", "Subscribed At"],
      ...filteredSubscribers.map((subscriber) => [
        subscriber.email,
        subscriber.name || "",
        subscriber.status,
        subscriber.subscribed_at ? new Date(subscriber.subscribed_at).toLocaleDateString() : "",
      ]),
    ].map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "subscribers.csv";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Subscribe Management | Roha Jewellery Admin</title>
        <meta name="description" content="Manage newsletter subscribers and email campaigns" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <AdminPageShell
        eyebrow="Audience retention"
        title="Subscribe Management"
        description="Track subscriber health, export records, and manage newsletter status from a refined admin workspace."
        actions={
          <button type="button" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary" onClick={exportSubscribers}>
            <FaDownload /> Export CSV
          </button>
        }
      >
        <section className="roha-admin-card-grid">
          <article className="roha-admin-stat roha-admin-stat--gold">
            <div className="roha-admin-stat__body">
              <span>Total subscribers</span>
              <strong>{subscribers.length}</strong>
              <p>All newsletter records.</p>
            </div>
          </article>
          <article className="roha-admin-stat roha-admin-stat--emerald">
            <div className="roha-admin-stat__body">
              <span>Active</span>
              <strong>{activeCount}</strong>
              <p>Currently subscribed contacts.</p>
            </div>
          </article>
          <article className="roha-admin-stat roha-admin-stat--ink">
            <div className="roha-admin-stat__body">
              <span>Unsubscribed</span>
              <strong>{inactiveCount}</strong>
              <p>Contacts removed from campaigns.</p>
            </div>
          </article>
          <article className="roha-admin-stat roha-admin-stat--slate">
            <div className="roha-admin-stat__body">
              <span>Engagement rate</span>
              <strong>{engagementRate}%</strong>
              <p>Active share of the list.</p>
            </div>
          </article>
        </section>

        <AdminSurface title="Subscribers list" subtitle={`${filteredSubscribers.length} matching subscribers`}>
          <div className="roha-admin-filterbar">
            <Input
              type="search"
              placeholder="Search subscribers..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <select className="form-select" value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>

          <div className="roha-admin-table-wrap">
            <Table responsive className="roha-admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Subscribed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="roha-admin-empty">Loading subscribers...</td>
                  </tr>
                ) : filteredSubscribers.length ? (
                  filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id}>
                      <td data-label="Email"><FaEnvelope /> {subscriber.email}</td>
                      <td data-label="Name">{subscriber.name || "-"}</td>
                      <td data-label="Status"><span className="roha-admin-pill">{subscriber.status}</span></td>
                      <td data-label="Subscribed At">
                        {subscriber.subscribed_at ? new Date(subscriber.subscribed_at).toLocaleDateString() : "-"}
                      </td>
                      <td data-label="Actions">
                        <div className="roha-admin-toolbar">
                          {subscriber.status === "active" ? (
                            <Button color="warning" size="sm" onClick={() => handleUnsubscribe(subscriber.email)}>
                              <FaUserSlash /> Unsubscribe
                            </Button>
                          ) : null}
                          <Button color="danger" size="sm" onClick={() => handleDelete(subscriber.id)}>
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="roha-admin-empty">No subscribers found.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </AdminSurface>
      </AdminPageShell>
    </>
  );
};

export default SubscribeManagement;
